import { useQuery } from '@tanstack/react-query';
import type { PortalStatementDto } from '@wally-reseller/shared';
import { Skeleton } from '@wally-reseller/ui/badge';
import { EmptyState, ErrorState } from '@wally-reseller/ui/empty-state';
import { cn } from '@wally-reseller/ui/utils';
import { FileText } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { api } from '@/lib/api';
import { useFormatDate, useFormatMoney, useFormatMonth, useT } from '@/lib/i18n';

const TONE: Record<PortalStatementDto['status'], string> = {
  published: 'bg-note text-foreground border-note-border',
  invoiced: 'bg-accent-soft text-accent',
  paid: 'bg-success/10 text-success',
};

export function StatementBadge({ status }: { status: PortalStatementDto['status'] }) {
  const t = useT();
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded border border-transparent px-1.5 py-px text-xs font-medium',
        TONE[status],
      )}
    >
      {t(`portal.statement.status.${status}`)}
    </span>
  );
}

/** Art. 3.4: the monthly statements Wally published for the partner. */
export function StatementsPage() {
  const t = useT();
  const fmt = useFormatDate();
  const money = useFormatMoney();
  const month = useFormatMonth();
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ['statements'], queryFn: api.statements });

  if (list.isError) return <ErrorState error={list.error} onRetry={() => list.refetch()} />;
  if (list.isPending) return <Skeleton className="h-40 w-full" />;
  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">{t('portal.statements.title')}</h1>
      {list.data.length === 0 ? (
        <EmptyState
          icon={FileText}
          title={t('portal.statements.empty')}
          className="rounded-lg border border-dashed border-border bg-background"
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-background">
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">{t('portal.statements.col.month')}</th>
                <th className="px-4 py-2 font-medium max-sm:hidden">
                  {t('portal.statements.col.number')}
                </th>
                <th className="px-4 py-2 text-right font-medium">
                  {t('portal.statements.col.commission')}
                </th>
                <th className="px-4 py-2 font-medium">{t('portal.statements.col.status')}</th>
                <th className="px-4 py-2 font-medium max-md:hidden">
                  {t('portal.statements.col.due')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {list.data.map((s) => (
                <tr
                  key={s.id}
                  className="cursor-pointer hover:bg-muted"
                  onClick={() => navigate(`/decomptes/${s.id}`)}
                >
                  <td className="px-4 py-2.5 font-medium">
                    <Link to={`/decomptes/${s.id}`} onClick={(e) => e.stopPropagation()}>
                      {month(s.month)}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground max-sm:hidden">{s.number}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
                    {money(s.totals.commission)}
                  </td>
                  <td className="px-4 py-2.5">
                    <StatementBadge status={s.status} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground max-md:hidden">
                    {s.status === 'paid' ? '—' : fmt(s.dueOn)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
